import {
    AgeError,
    Booker,
    GetCourseDetailsByIdPublicQuery,
    NewBookingInput,
    UpdateBookerAccountEndUserAppInput,
    useBookerExistsAsAttendeeEndUserAppQuery,
    useCheckAttendeesAgeAtCourseStartQuery,
    useUpdateBookerAccountEndUserAppMutation,
} from "../../../../generated/graphql"
import React, { FC, memo, useEffect, useState } from "react"
import PanelCard from "../../../../views/PanelCard/PanelCard"
import { Button, Form, Input } from "antd"
import { NewBookingInputKeys } from "../../BookingSummary/BookingSummary"
import moment from "moment"
import { isDefined } from "../../../../helpers/typeScriptHelpers"
import BirthdayInput from "../../../../views/BirthdayInput/BirthdayInput"
import { attendeeCheck } from "../../../../views/SelectAttendeeTable/checkAttendee"
import { green, red } from "@ant-design/colors"
import { CheckCircleOutlined } from "@ant-design/icons"
import { useMediaSize } from "../../../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import i18n from "../../../../i18n"

type SummaryLeftSelfBookerProps = {
    booker: Booker
    updateBookingData: (key: NewBookingInputKeys, value: any) => void
    setBookerAsAttendee: (bookerId: string) => void
    refetchBooker: () => void
    bookingData?: NewBookingInput
    setAttendeeAgeDoesFitCourse: (arg0: boolean) => void
    isCourseWithPreRequisites?: boolean
    bookingRestriction: GetCourseDetailsByIdPublicQuery["courseByIdPublic"]["bookingRestriction"]
    isMemberOnlyCourse?: boolean
    labelStyle?: React.CSSProperties
    entryStyle?: React.CSSProperties
}

const SummaryLeftSelfBooker: FC<SummaryLeftSelfBookerProps> = ({
    booker,
    updateBookingData,
    setBookerAsAttendee,
    refetchBooker,
    bookingData,
    setAttendeeAgeDoesFitCourse,
    isCourseWithPreRequisites,
    bookingRestriction,
    isMemberOnlyCourse,
    labelStyle,
    entryStyle,
}) => {
    const mediaSize = useMediaSize()
    const { media } = mediaSize
    const [bookersBirthDate, setBookersBirthDate] = useState<string | undefined>(booker.birthday)
    // CHECK ATTENDEES AGE -------------------------------------------------------------------------------------------------------------
    const [selectedAttendeesAgeOk, setSelectedAttendeesAgeOk] = useState<boolean>(true)
    const courseId = bookingData?.courseId
    const attendeeId = bookingData?.attendeeId

    const { data: attendeesAgeAtCourseStartData, loading: loadingAttendeesAge, refetch: refetchAttendeesAge } =
        useCheckAttendeesAgeAtCourseStartQuery({
            skip: !courseId || !attendeeId,
            variables: { courseId: courseId ?? "", attendeeId: (attendeeId as string) ?? "" },
            fetchPolicy: "network-only",
        })

    const ageAtCourseStartData = attendeesAgeAtCourseStartData?.checkAttendeesAgeAtCourseStart

    useEffect(() => {
        if (ageAtCourseStartData && ageAtCourseStartData.ageError !== undefined) {
            setSelectedAttendeesAgeOk(ageAtCourseStartData.ageError === null)
            setAttendeeAgeDoesFitCourse(ageAtCourseStartData.ageError === null)
        }
    }, [ageAtCourseStartData, setAttendeeAgeDoesFitCourse])

    useEffect(() => {
        if (!courseId || !attendeeId || !refetchAttendeesAge) return
        setTimeout(() => {
            refetchAttendeesAge()
        }, 10)
    }, [attendeeId, courseId, refetchAttendeesAge])

    // Solange die Altersprüfung lädt, gilt das Alter nicht als passend (W-28501).
    useEffect(() => {
        setAttendeeAgeDoesFitCourse(selectedAttendeesAgeOk && !loadingAttendeesAge)
    }, [setAttendeeAgeDoesFitCourse, selectedAttendeesAgeOk, loadingAttendeesAge])

    // CHECK IF BOOKER IS ALREADY AN ATTENDEE ------------------------------------------------------------------------------------------------
    const { data, loading } = useBookerExistsAsAttendeeEndUserAppQuery({
        skip: !booker.id,
        variables: { bookerId: booker.id },
        fetchPolicy: "network-only",
    })
    const bookerExistsAsAttendee = data?.bookerExistsAsAttendeeEndUserApp === true
    const [updateBooker, { loading: updatingBooker }] = useUpdateBookerAccountEndUserAppMutation()
    const [done, setDone] = useState<boolean>(false)

    // NOT SURE IF THIS IS NEEDED
    useEffect(() => {
        refetchBooker()
    }, [refetchBooker])

    useEffect(() => {
        const handleBookerAsAttendee = ({ booker }: { booker: Booker }) => {
            const attendees = booker.attendees
            // Defensive check for booker.customer
            const bookerCustomerId = booker.customer?.id
            if (!bookerCustomerId) {
                console.error("Booker customer ID is undefined", booker)
                return
            }
            // Defensive check for attendee.customer
            const bookersAttendee = attendees?.find((attendee) => attendee.customer?.id === bookerCustomerId)

            if (bookersAttendee /* && !attendeeIsUpToDate */ && bookerExistsAsAttendee) {
                updateBookingData("attendeeId", bookersAttendee.id)
                // console.log("001 Booker is already an attendee")
                if (done !== true) setDone(true)
            } else {
                setBookerAsAttendee(booker.id)
                // console.log("002 Booker has birthday but is not an attendee")
                if (done !== true) setDone(true)
            }
        }

        if (!done && booker && !loading && !updatingBooker && booker.birthday) {
            handleBookerAsAttendee({ booker })
        }
    }, [
        booker,
        updateBookingData,
        setBookerAsAttendee,
        done,
        loading,
        bookingData,
        bookerExistsAsAttendee,
        updatingBooker,
    ])

    const updateBookersBirthDate = async (date: string) => {
        const isDateValid = moment(date).isValid()
        if (!isDateValid) {
            return
        }
        const updateBookerEndUserAppData: UpdateBookerAccountEndUserAppInput = {
            id: booker.id,
            birthday: date,
        }
        try {
            await updateBooker({
                variables: {
                    updateBookerEndUserAppData,
                    fromEndUserApp: true,
                },
            }).then((res) => {
                refetchBooker()
                // console.log("updateBookersBirthDate", res)
            })
        } catch (error) {
            console.error("updateBookersBirthDate", error)
            // TODO handle error
        }
    }

    const bookerBirthdayInput = (/* { birthday }: { birthday?: string } */) => {
        const buttonWidth = 80
        const birthDayError = !booker.birthday ? "error" : undefined
        const attendees = booker.attendees
        const bookersAttendee = attendees?.find((attendee) => attendee.customer.id === booker.customerId)

        const getHelpText = () => {
            if (!bookersBirthDate) {
                return "Bitte geben Sie Ihr Geburtsdatum an."
            }
            if (
                bookersAttendee &&
                courseId &&
                attendeeCheck(bookersAttendee, courseId, isCourseWithPreRequisites, bookingRestriction).allreadyBooked
            ) {
                return "Dieser Teilnehmer ist bereits in diesem Kurs gebucht"
            }
            if (!selectedAttendeesAgeOk) {
                switch (ageAtCourseStartData?.ageError) {
                    case AgeError.TooYoung:
                        return "Der Teilnehmer ist zu jung für diesen Kurs"
                    case AgeError.TooOld:
                        return "Der Teilnehmer ist zu alt für diesen Kurs"
                    default:
                        return undefined
                }
            }
            if (isMemberOnlyCourse && !bookersAttendee?.member) {
                return "Dieser Kurs ist exclusiv für Mitglieder buchbar"
            }
            const prerequisiteCheck =
                isCourseWithPreRequisites && bookersAttendee && courseId
                    ? attendeeCheck(bookersAttendee, courseId, isCourseWithPreRequisites, bookingRestriction)
                    : undefined
            if (prerequisiteCheck?.waitlistOnly) {
                return i18n.t("bookingStepSummary.prerequisiteWaitlist.attendeeHint", {
                    defaultValue: "Voraussetzung fehlt, Eintrag nur auf die Warteliste möglich",
                })
            }
            if (prerequisiteCheck?.bookedIntoPreRequisite === false) {
                return "Dieser Teilnehmer ist nicht in den Voraussetzungskursen"
            }
            return undefined
        }

        return (
            <Form.Item
                help={<div style={{ color: red[4], paddingLeft: 5 }}>{getHelpText()}</div>}
                style={{ width: "100%" }}
                required
            >
                <Input.Group compact>
                    <BirthdayInput
                        style={{ width: `calc(100% - ${buttonWidth}px)`, height: 32 }}
                        data-testid='AttendeeCreateModalBirthday'
                        size='middle'
                        onChange={(e) => {
                            setBookersBirthDate(e.target.value || undefined)
                        }}
                        onBlur={async () => {
                            if (bookersBirthDate) {
                                updateBookersBirthDate(bookersBirthDate).then(() => {
                                    refetchAttendeesAge({
                                        attendeeId: attendeeId as string,
                                        courseId: courseId as string,
                                    })
                                })
                            }
                        }}
                        value={isDefined(bookersBirthDate) ? moment(bookersBirthDate).format("YYYY-MM-DD") : ""}
                        status={booker.birthday ? undefined : "error"}
                    />
                    <Button
                        type={bookersBirthDate && bookersBirthDate !== booker.birthday ? "primary" : "default"}
                        style={{
                            width: buttonWidth,
                            border: birthDayError ? "1px solid red" : undefined,
                            borderLeft: "unset",
                        }}
                        onClick={async () => {
                            if (bookersBirthDate) {
                                updateBookersBirthDate(bookersBirthDate).then(() => {
                                    // refetchAttendeesAge()
                                    refetchAttendeesAge({
                                        attendeeId: attendeeId as string,
                                        courseId: courseId as string,
                                    })
                                })
                            }
                        }}
                        // loading={updatingBooker || loadingAttendeesAgeAtStart}
                        // loading={loadingAttendeesAgeAtStart}
                        loading={updatingBooker}
                    >
                        OK
                    </Button>
                </Input.Group>
            </Form.Item>
        )
    }

    const actualLabelStyle = { ...labelStyle, gridColumn: "1/span 1", dispaly: media === "sm" ? "none" : "block" }

    return (
        <>
            <div>{selectedAttendeesAgeOk ? "korrektes Alter" : "Alter passt nicht"}</div>
            <PanelCard
                title={
                    <span style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                        {selectedAttendeesAgeOk ? (
                            <>
                                Geburtsdatum <CheckCircleOutlined style={{ color: green[4] }} />
                            </>
                        ) : (
                            "Geburtsdatum"
                        )}
                    </span>
                }
                columns={1}
                style={{
                    fontWeight: 500,
                    borderColor: "lightgrey",
                }}
                smallTitle
                gapAfter
            >
                {bookerBirthdayInput()}
            </PanelCard>
            <PanelCard
                title={"Anschrift"}
                columns={1}
                style={{
                    fontWeight: 500,
                    borderColor: "lightgrey",
                }}
                smallTitle
                gapAfter
            >
                <div>{booker.street + " " + booker.houseNumber}</div>
                <div>{booker.postCode + " " + booker.city}</div>
            </PanelCard>
            <PanelCard
                title={"Kontaktdaten"}
                columns={4}
                style={{
                    fontWeight: 500,
                    borderColor: "lightgrey",
                }}
                smallTitle
            >
                {media !== "sm" && <div style={{ ...actualLabelStyle }}>{"Telefon"}</div>}
                <div
                    style={{
                        overflowWrap: "break-word",
                        ...entryStyle,
                        gridColumn: media === "sm" ? "1 / -1" : "2 / -1",
                    }}
                >
                    {booker.phoneNumber}
                </div>
                {media !== "sm" && <div style={{ ...actualLabelStyle }}>{"Mobil"}</div>}
                <div
                    style={{
                        overflowWrap: "break-word",
                        ...entryStyle,
                        gridColumn: media === "sm" ? "1 / -1" : "2 / -1",
                    }}
                >
                    {booker.mobileNumber}
                </div>
                {media !== "sm" && <div style={{ ...actualLabelStyle }}>{"Email"}</div>}
                <div
                    style={{
                        overflowWrap: "break-word",
                        ...entryStyle,
                        gridColumn: media === "sm" ? "1 / -1" : "2 / -1",
                    }}
                >
                    {booker.email}
                </div>
            </PanelCard>
        </>
    )
}

export default memo(SummaryLeftSelfBooker)
